import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Delete a Sale / Pro-forma / Invoice from a detail view.
 *  - Draft records: any signed-in user can delete
 *  - Non-draft records: only Super Admin can delete
 *  - Super Admin sees a "Force cascade" option that bypasses all blockers and
 *    removes every linked child (allocations, receipts, sub-documents).
 *
 * Props:
 *  - resource: "sales" | "proformas" | "invoices" (path segment)
 *  - id: record id
 *  - status: record status string
 *  - label: friendly label for confirmation copy (e.g., "sale S-2025-00042")
 *  - onDeleted: callback fired after successful delete
 *  - testId: root data-testid for the trigger button
 */
export function DeleteRecordButton({ resource, id, status, label, onDeleted, testId }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [force, setForce] = useState(false);

  const isDraft = status === "draft";
  const isSuper = user?.role === "superadmin";
  const canDelete = isDraft || isSuper;

  if (!canDelete) return null;

  const doDelete = async () => {
    setBusy(true);
    try {
      const url = force && isSuper ? `/${resource}/${id}?force=true` : `/${resource}/${id}`;
      const { data } = await api.delete(url);
      if (data?.cascade) {
        const parts = Object.entries(data.cascade).filter(([, v]) => v > 0)
          .map(([k, v]) => `${v} ${k}`).join(", ");
        toast.success(parts ? `Deleted (cascade: ${parts})` : `Deleted ${label || "record"}`);
      } else {
        toast.success(`Deleted ${label || "record"}`);
      }
      setOpen(false);
      setForce(false);
      onDeleted?.();
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      if (!force && isSuper && err.response?.status === 409) {
        toast.error(`${detail} — tick "Force cascade" to override.`);
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  const nonDraftWarn = !isDraft;

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) setForce(false); } }}>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
          data-testid={testId || `delete-${resource}-button`}
        >
          <Trash2 className="w-4 h-4 mr-1.5" /> Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`${testId || `delete-${resource}`}-confirm`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {label || "record"}?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes the record and writes an entry to the audit log.
            {nonDraftWarn && (
              <span className="block mt-2 text-rose-700 font-medium">
                This record is not a draft ({status?.toUpperCase()}). Deletion is only allowed for Super Admin
                and will be blocked if any linked allocations, invoices or receipts exist.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {isSuper && (
          <label className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm">
            <Checkbox checked={force} onCheckedChange={setForce} data-testid={`${testId || `delete-${resource}`}-force`} />
            <span>
              <span className="font-semibold text-rose-800">Force cascade</span>
              <span className="block text-xs text-rose-700 mt-0.5">
                Removes every linked child record (allocations, receipts, sub-invoices, pro-formas).
                Use this to clean up dirty data — no blocker will stop the delete.
              </span>
            </span>
          </label>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid={`${testId || `delete-${resource}`}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); doDelete(); }}
            disabled={busy}
            className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
            data-testid={`${testId || `delete-${resource}`}-confirm-action`}
          >
            {busy ? "Deleting…" : force && isSuper ? "Force delete" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
